import {InferGetStaticPropsType} from 'next';
import Image from 'next/image';
import React, {useMemo} from 'react';
import Row from 'src/features/common/Row';

import balance from '../../public/img/gameinfo/balance@4x.png';
import game_screenshot_1 from '../../public/img/gameinfo/game_screenshot_1.png';
import game_screenshot_2 from '../../public/img/gameinfo/game_screenshot_2.png';
import help from '../../public/img/gameinfo/help@4x.png';
import scenarios from '../../public/img/gameinfo/scenarios@4x.png';
import Col from '../features/common/Col';
import {useEnvironment} from '../features/common/EnvironmentProvider';
import {getCmsEntry} from '../features/strapi';
import {ApiFooterFooter, ApiGameinfoGameinfo} from '../features/strapi/generated/contentTypes';
import Button from '../features/tool/components/buttons/Button';
import Footer from '../features/tool/components/Footer';
import {getToolLayout} from '../features/tool/components/Layout';
import Newsletter from '../features/tool/components/Newsletter';
import ReviewList from '../features/tool/components/ReviewsList';
import RichText from '../features/tool/components/RichText';
import SectionWithImage from '../features/tool/components/SectionWithImage';
import {buildStaticPropsResponse} from '../features/tool/util/dataFetch';
import {FeedbackSquareIcon} from '../resources/Icons';
import URLS from '../urls';
import {NextPageWithLayout} from './_app';

const GamePage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = ({gameinfo, footer}) => {
  const environment = useEnvironment();
  const reviews_count = gameinfo.reviews_list?.length;
  const reviews_average = useMemo(
    () =>
      !gameinfo.reviews_list?.length
        ? 0
        : gameinfo.reviews_list.reduce((previousValue, currentValue) => previousValue + (currentValue.stars ?? 0), 0) /
          gameinfo.reviews_list.length,
    [gameinfo.reviews_list]
  );

  return (
    <>
      <div className="w-full py-xl px-xs bg-primary-700">
        <SectionWithImage
          title={gameinfo.intro_title}
          description={gameinfo.intro_body}
          image={<Image src={game_screenshot_1} alt={'Game screenshot 1'} className={'mx-auto'} />}
          buttonLabel={'Play now!'}
          buttonHref={URLS.play}
          newTab
          dark
          textSize={'small'}
          className={'max-w-limit mx-auto'}
        />
      </div>
      <div className="w-full py-xl px-xs">
        <SectionWithImage
          reversed
          title={gameinfo.plot_title}
          description={gameinfo.plot_body}
          image={<Image src={game_screenshot_2} alt={'Game screenshot 2'} className={'mx-auto'} />}
          buttonLabel={'Play now!'}
          buttonHref={URLS.play}
          newTab
          textSize={'small'}
          className={'max-w-limit mx-auto'}
        />
      </div>
      <Col className="w-full py-xl px-xs bg-primary-100 gap-lg">
        <h1 className="max-w-limit mx-auto text-3xl font-semibold leading-tight text-center">
          {gameinfo.features_title}
        </h1>
        <Row className="max-w-limit mx-auto gap-sm mobile:flex-col mobile:gap-lg">
          {gameinfo.features?.map((feature, idx) => (
            <Col key={idx} className={'basis-0 grow gap-2'}>
              {feature.icon === 'balance' && (
                <Image src={balance} alt={'Balance icon'} className={'w-[20.9rem] mx-auto '} />
              )}
              {feature.icon === 'scenarios' && (
                <Image src={scenarios} alt={'Scenarios icon'} className={'w-[20.9rem] mx-auto'} />
              )}
              {feature.icon === 'help' && <Image src={help} alt={'Help icon'} className={'w-[20.9rem] mx-auto'} />}
              <h2 className={'mt-md text-lg font-semibold'}>{feature.title}</h2>
              <RichText content={feature.body} />
            </Col>
          ))}
        </Row>
      </Col>
      <div className="w-full py-lg px-xs bg-primary-700">
        <Row className="max-w-limit mx-auto items-center gap-sm text-white mobile:flex-wrap">
          <FeedbackSquareIcon className={'shrink-0'} />
          {gameinfo.feedback_text}
          <Button
            className={'border-white shrink-0'}
            variant="primary"
            href={gameinfo.feedback_form_url}
            target={'_blank'}
            rel={'noopener noreferrer'}
            accessory={'forward'}
          >
            <span className={'whitespace-nowrap'}>{gameinfo.feedback_button_label}</span>
          </Button>
        </Row>
      </div>
      <Col className="w-full max-w-limit mx-auto px-xs py-xl mobile:my-sm">
        <ReviewList
          className="w-full"
          title={gameinfo.reviews_title}
          count={reviews_count || 0}
          average={reviews_average}
          list={
            gameinfo.reviews_list?.map(review => ({
              name: review.name,
              text: review.text,
              rating: review.stars,
            })) ?? []
          }
        />
      </Col>
      {environment && <Newsletter data={environment.newsletter} />}
      <Footer data={footer} />
    </>
  );
};

export const getStaticProps = async () => {
  return buildStaticPropsResponse({
    ...(await getCmsEntry<ApiGameinfoGameinfo, 'gameinfo'>({
      path: 'gameinfo',
      params: {populate: '*'},
      processDataMap: {intro_body: 'markdown', plot_body: 'markdown'},
    })),
    ...(await getCmsEntry<ApiFooterFooter, 'footer'>({path: 'footer'})),
  });
};

export default GamePage;

GamePage.getLayout = getToolLayout;

GamePage.title = 'Cropper game';
